import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { fetchNodeDetails, fetchNodeTelemetry } from '../../services/api';
import Card from '../ui/Card';
import Spinner from '../ui/Spinner';
import ErrorMessage from '../ui/ErrorMessage';
import TelemetryChart from '../charts/TelemetryChart';

/**
 * Props for the NodeDetailsPanel component.
 */
interface NodeDetailsPanelProps {
  nodeId: number;
  onClose: () => void;
}

/**
 * NodeDetailsPanel component.
 * Displays detailed information about a selected node.
 */
const NodeDetailsPanel: React.FC<NodeDetailsPanelProps> = ({ nodeId, onClose }) => {
  // State for active tab
  const [activeTab, setActiveTab] = useState<'details' | 'telemetry'>('details');

  // Fetch node details data
  const { 
    data: nodeDetails, 
    isLoading: isLoadingDetails, 
    isError: isErrorDetails,
    error: detailsError,
    refetch: refetchDetails
  } = useQuery({
    queryKey: ['nodeDetails', nodeId],
    queryFn: () => fetchNodeDetails(nodeId),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Fetch node telemetry data
  const { 
    data: telemetryData, 
    isLoading: isLoadingTelemetry, 
    isError: isErrorTelemetry,
    error: telemetryError,
    refetch: refetchTelemetry
  } = useQuery({
    queryKey: ['nodeTelemetry', nodeId],
    queryFn: () => fetchNodeTelemetry(nodeId, 24), // Get 24 hours of telemetry
    refetchInterval: 60000, // Refetch every minute
    enabled: activeTab === 'telemetry', // Only fetch when telemetry tab is active
  });

  // Handle retry
  const handleRetry = () => {
    if (activeTab === 'details') {
      refetchDetails();
    } else {
      refetchTelemetry();
    }
  };

  // Render loading state
  if ((activeTab === 'details' && isLoadingDetails) || 
      (activeTab === 'telemetry' && isLoadingTelemetry)) {
    return (
      <Card title="Node Details" onClose={onClose}>
        <LoadingContainer>
          <Spinner size="md" />
          <LoadingText>Loading node {activeTab}...</LoadingText>
        </LoadingContainer>
      </Card>
    );
  }

  // Render error state
  if ((activeTab === 'details' && isErrorDetails) || 
      (activeTab === 'telemetry' && isErrorTelemetry)) {
    return (
      <Card title="Node Details" onClose={onClose}>
        <ErrorMessage 
          message={`Failed to load node ${activeTab}`} 
          details={(activeTab === 'details' ? detailsError : telemetryError)?.toString()}
          onRetry={handleRetry}
        />
      </Card>
    );
  }

  // Format the last seen time
  const formatLastSeen = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get the most recent telemetry data point
  const getLatestTelemetry = () => {
    if (!telemetryData || telemetryData.length === 0) return null;
    return telemetryData[0]; // Assuming the data is sorted with newest first
  };

  return (
    <DetailsPanelContainer>
      <PanelHeader>
        <Title>{`Node: ${nodeDetails?.longName || nodeId.toString(16)}`}</Title>
        <CloseButton onClick={onClose}>&times;</CloseButton>
      </PanelHeader>
      
      <TabsContainer>
        <Tab 
          $active={activeTab === 'details'}
          onClick={() => setActiveTab('details')}
        >
          Details
        </Tab>
        <Tab 
          $active={activeTab === 'telemetry'}
          onClick={() => setActiveTab('telemetry')}
        >
          Telemetry
        </Tab>
      </TabsContainer>

      <PanelContent>
        {activeTab === 'details' && nodeDetails && (
          <>
            <DetailsGrid>
              <DetailItem>
                <DetailLabel>Node ID</DetailLabel>
                <DetailValue>{nodeDetails.nodeHex}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Short Name</DetailLabel>
                <DetailValue>{nodeDetails.shortName}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Hardware</DetailLabel>
                <DetailValue>{nodeDetails.hardwareModel}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Last Seen</DetailLabel>
                <DetailValue>{formatLastSeen(nodeDetails.lastSeen)}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Battery</DetailLabel>
                <BatteryIndicator level={nodeDetails.batteryLevel}>
                  {nodeDetails.batteryLevel}%
                </BatteryIndicator>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Voltage</DetailLabel>
                <DetailValue>{nodeDetails.voltage.toFixed(2)}V</DetailValue>
              </DetailItem>
            </DetailsGrid>
            
            <SectionTitle>Location</SectionTitle>
            <LocationGrid>
              <DetailItem>
                <DetailLabel>Latitude</DetailLabel>
                <DetailValue>{nodeDetails.latitude.toFixed(6)}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Longitude</DetailLabel>
                <DetailValue>{nodeDetails.longitude.toFixed(6)}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Altitude</DetailLabel>
                <DetailValue>{nodeDetails.altitude}m</DetailValue>
              </DetailItem>
            </LocationGrid>
          </>
        )}

        {activeTab === 'telemetry' && (
          <TelemetryContainer>
            {telemetryData && telemetryData.length > 0 ? (
              <>
                <TelemetryLatest>
                  <SectionTitle>Latest Readings</SectionTitle>
                  <TelemetryGrid>
                    <TelemetryItem>
                      <TelemetryLabel>Battery</TelemetryLabel>
                      <TelemetryValue>
                        <BatteryIndicator level={getLatestTelemetry()?.batteryLevel || 0}>
                          {getLatestTelemetry()?.batteryLevel || 0}%
                        </BatteryIndicator>
                      </TelemetryValue>
                    </TelemetryItem>
                    
                    <TelemetryItem>
                      <TelemetryLabel>Voltage</TelemetryLabel>
                      <TelemetryValue>
                        {getLatestTelemetry()?.voltage.toFixed(2) || 0}V
                      </TelemetryValue>
                    </TelemetryItem>
                    
                    {getLatestTelemetry()?.temperature && (
                      <TelemetryItem>
                        <TelemetryLabel>Temperature</TelemetryLabel>
                        <TelemetryValue>
                          {getLatestTelemetry()?.temperature.toFixed(1) || 0}°C
                        </TelemetryValue>
                      </TelemetryItem>
                    )}
                    
                    {getLatestTelemetry()?.humidity && (
                      <TelemetryItem>
                        <TelemetryLabel>Humidity</TelemetryLabel>
                        <TelemetryValue>
                          {getLatestTelemetry()?.humidity.toFixed(1) || 0}%
                        </TelemetryValue>
                      </TelemetryItem>
                    )}
                  </TelemetryGrid>
                </TelemetryLatest>
                
                <SectionTitle>History (24h)</SectionTitle>
                <TelemetryChart data={telemetryData} height={250} />
              </>
            ) : (
              <EmptyState>
                <EmptyText>No telemetry data available</EmptyText>
              </EmptyState>
            )}
          </TelemetryContainer>
        )}
      </PanelContent>
    </DetailsPanelContainer>
  );
};

/**
 * Styled container for the details panel.
 */
const DetailsPanelContainer = styled.div`
  display: flex !important; /* Force display as flex */
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.radii.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
  opacity: 1 !important; /* Ensure visibility */
  height: auto !important; /* Let content determine height */
  min-height: 320px; /* Minimum height to ensure visibility */
  position: relative; /* For close button absolute positioning */
`;

/**
 * Styled header for the details panel.
 */
const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  background: ${({ theme }) => theme.colors.background};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

/**
 * Styled title for the details panel.
 */
const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

/**
 * Styled close button for the details panel.
 */
const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.7;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
`;

/**
 * Styled content for the details panel.
 */
const PanelContent = styled.div`
  padding: ${({ theme }) => theme.space.sm};
  overflow-y: auto;
`;

/**
 * Styled tabs container.
 */
const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

/**
 * Styled tab.
 */
interface TabProps {
  $active: boolean;
}

const Tab = styled.button<TabProps>`
  padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  margin-bottom: -1px;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  color: ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.textLight};
  border-bottom: 2px solid ${({ theme, $active }) => $active ? theme.colors.primary : 'transparent'};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
  
  &:focus {
    outline: none;
  }
`;

/**
 * Styled grid container for details items.
 */
const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.space.md};
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

/**
 * Styled grid container for location details.
 */
const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.space.md};
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

/**
 * Styled container for individual detail items.
 */
const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * Styled label for detail items.
 */
const DetailLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.space.xs};
`;

/**
 * Styled value for detail items.
 */
const DetailValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  word-break: break-word;
`;

/**
 * Styled section title.
 */
const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

/**
 * Styled battery indicator.
 * Changes color based on battery level.
 */
const BatteryIndicator = styled.div<{ level: number }>`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme, level }) => {
    if (level > 50) return theme.colors.success;
    if (level > 20) return theme.colors.warning;
    return theme.colors.danger;
  }};
`;

/**
 * Styled container for telemetry data.
 */
const TelemetryContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * Styled container for latest telemetry readings.
 */
const TelemetryLatest = styled.div`
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

/**
 * Styled grid for telemetry items.
 */
const TelemetryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.space.md};
`;

/**
 * Styled telemetry item.
 */
const TelemetryItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.space.sm};
  background-color: ${({ theme }) => theme.colors.backgroundDark};
  border-radius: ${({ theme }) => theme.radii.md};
`;

/**
 * Styled telemetry label.
 */
const TelemetryLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.space.xs};
`;

/**
 * Styled telemetry value.
 */
const TelemetryValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

/**
 * Styled container for loading state.
 */
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space.lg};
  gap: ${({ theme }) => theme.space.md};
`;

/**
 * Styled text for loading state.
 */
const LoadingText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textLight};
`;

/**
 * Styled container for empty state.
 */
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space.lg};
`;

/**
 * Styled text for empty state.
 */
const EmptyText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.textLight};
`;

export default NodeDetailsPanel; 